import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import {sendMailInvoiceApi} from 'api/invoiceApis'
import {Button, TableCell} from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import TableCustomerSearch from 'pages/CustomerManagement/InvoiceList/TableCustomerSearch'
import useStyles from 'components/Table/useTableStyles'
import Pagination from 'components/molcules/Pagination'
import useCustomerRegistration from 'features/invoice/useInvoiceList'
import {DetailedInvoiceTarget} from 'features/invoice/InvoiceListSlice'
import {dateToPeriodString, getDefaultPeriodDate} from 'helpers/dateHelper'
import {getQueryFromUrl} from 'helpers/commonHelper'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import RouteConstant from 'constants/RouteConstant'
import {formatPriceNumber} from 'helpers/uiHelper'

interface RenderTableRowProps {
  item: DetailedInvoiceTarget
  onClick: (item: DetailedInvoiceTarget) => void
  isSelected: (item: DetailedInvoiceTarget) => boolean
}

function RenderTableRow({item, onClick, isSelected}: RenderTableRowProps) {
  const {t} = useTranslation()
  const history = useHistory()
  const labelId = `enhanced-table-checkbox-${item.orgId}`
  const classes = useStyles()
  const checked = isSelected(item)
  const handleClick = () => onClick(item)

  const handleToInvoiceView = (item: DetailedInvoiceTarget) => {
    history.push(
      `${RouteConstant.INVOICE_VIEW.path}?orgId=${item.orgId}&billingMonth=${item.billingMonth}`,
    )
  }

  // const handleGoToDetailOrg = (id: number | undefined) => {
  //   if (typeof id === 'number') {
  //     history.push(`${RouteConstant.MEMBER_EDIT.path}?orgId=${id}`)
  //   }
  // }

  return (
    <TableRow hover onClick={handleClick}>
      <TableCell padding='checkbox'>
        <Checkbox
          checked={checked}
          disabled={!item.billingFileId}
          inputProps={{'aria-labelledby': labelId}}
        />
      </TableCell>
      <TableCell align='center'>{item.orgId}</TableCell>
      <TableCell align='center'>{item.countryName}</TableCell>
      <TableCell
        align='center'
        // onClick={() => handleGoToDetailOrg(Number(item.orgId))}
        // className={classes.buttonActiveLink}
      >
        {item.orgName && item.orgName.length > 50
          ? `${item.orgName.substring(0, 50)}...`
          : item.orgName}
      </TableCell>
      <TableCell align='center'>
        {t('IGetFullName', {
          firstName: item?.orgUserFirstname ?? '',
          lastName: item?.orgUserLastname ?? '',
        }).trim()}
      </TableCell>
      <TableCell align='center'>
        {item.email && item.email.length > 50
          ? `${item.email.substring(0, 50)}...`
          : item.email}
      </TableCell>
      <TableCell align='center'>
        {formatPriceNumber(item.billingPrice ?? 0)}
      </TableCell>
      <TableCell align='center'>{item.billingMonth}</TableCell>
      <TableCell align='center'>
        {item.reportFileUrl
          ? t('IActiveSendMailPDF')
          : t('IActiveSendMailNoPDF')}
      </TableCell>
      <TableCell
        align='center'
        onClick={() => handleToInvoiceView(item)}
        className={classes.buttonActiveLink}
      >
        {item.issued ? t('ITransmissionComplete') : t('INotCent')}
      </TableCell>
    </TableRow>
  )
}

function PageTable() {
  const {t} = useTranslation()
  const location = useLocation()
  const classes = useStyles()
  const [selected, setSelected] = useState<DetailedInvoiceTarget[]>([])
  const selectedId = useMemo(
    () => selected.map((item) => item.orgId),
    [selected],
  )
  const selection = getDefaultPeriodDate()
  const {loading, query, paging, pagingInfo, items, onSearch, isConfirmed} =
    useCustomerRegistration()
  const setPageIndex = (pageNumber: number) => {
    onSearch({
      ...query,
      page: pageNumber,
      size: paging.pageSize,
    })
  }
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()

  const onSizeChange = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const pageSize = parseInt(event.target.value as string, 10)
    onSearch({...query, page: 0, size: pageSize})
  }

  const activeSendMail = async () => {
    if (!selected.length) return
    const listId = selected.map((item) => item.orgId)
    try {
      await sendMailInvoiceApi(listId.sort().join(','))
      onSuccessModalOpen(t('IEmailHasBeenSent'))
    } catch (error) {
      const err = JSON.parse(JSON.stringify(error))
      onFailureModalOpen(
        err.err.debugMessage
          ? err.err.debugMessage
          : t('ISelectDataInProgress'),
      )
    } finally {
      onSearch({...query})
    }
  }

  const handleRowClick = (item: DetailedInvoiceTarget) => {
    if (!item.billingFileId) return
    const selectedIndex = selectedId.indexOf(item.orgId)

    let newSelected: DetailedInvoiceTarget[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    setSelected(newSelected)
  }

  const isRowSelected = (item: DetailedInvoiceTarget) => {
    if (!item.billingFileId) return false
    return selectedId.indexOf(item.orgId) !== -1
  }

  const filteredItems = items.filter((item) => {
    if (isConfirmed === true && item?.issued) return true
    if (isConfirmed === false && !item?.issued) return true
    if (isConfirmed === null) return true
    return false
  })
  const successItems = items.filter((item) => item.orgId)
  const emptyRows = items === null ? 5 : 5 - items.length

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(items.filter((item) => item.orgId))
      return
    }
    setSelected([])
  }

  useEffect(() => {
    const initQuery = getQueryFromUrl(location.search)
    onSearch({
      ...query,
      search: {orgName: ''},
      period: dateToPeriodString(selection),
      paging: {pageNumber: 0, pageSize: 10},
      ...initQuery,
      type: 'All',
    })
  }, [])

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <TableCustomerSearch isRoot={false} query={query} onSearch={onSearch} />
        <div style={{display: 'flex', justifyContent: 'end'}}>
          <Button
            variant='contained'
            color='primary'
            disableElevation
            type='submit'
            onClick={() => activeSendMail()}
            className={classes.buttonSendInvoice}
          >
            {t('ICustomerSendInvoice')}
          </Button>
        </div>
        <Table className={classes.table} size='small'>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell align='center' padding='none'>
                <Checkbox
                  indeterminate={
                    selected.length > 0 && selected.length < successItems.length
                  }
                  checked={
                    successItems.length > 0 &&
                    selected.length === successItems.length
                  }
                  onChange={handleSelectAllClick}
                  inputProps={{'aria-label': 'select all desserts'}}
                />
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerNumber')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerNation')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerOrganizationName')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerRepresentative')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerMail')}
              </TableCell>

              <TableCell align='center' padding='none'>
                {t('ICustomerAmount')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerInstitution')}
              </TableCell>

              <TableCell align='center' padding='none'>
                {t('IActiveSendMail')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('ICustomerInvoice')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => (
              <RenderTableRow
                key={item.orgId}
                item={item}
                onClick={handleRowClick}
                isSelected={isRowSelected}
              />
            ))}
            {emptyRows > 0 && (
              <TableRow
                style={{height: 43 * emptyRows, backgroundColor: '#F9F9FB'}}
              >
                <TableCell colSpan={14} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination
          totalPageCount={pagingInfo.totalPages}
          currentPageIndex={query.page}
          itemCountPerPage={query.size}
          setCurrentPageIndex={setPageIndex}
          loading={loading}
          onItemCountPerPageChanged={onSizeChange}
        />
      </TableContainer>
    </>
  )
}

export default PageTable
